import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

/**
 * Settings state interface
 */
export interface SettingsState {
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
  preferences: {
    language: string;
    timezone: string;
    dateFormat: string;
  };
  privacy: {
    profileVisibility: 'public' | 'private' | 'friends';
    showEmail: boolean;
    showPhone: boolean;
  };
  accessibility: {
    fontSize: 'small' | 'medium' | 'large';
    highContrast: boolean;
    reduceMotion: boolean;
  };
}

/**
 * Initial settings state
 */
const initialState: SettingsState = {
  notifications: {
    email: true,
    push: true,
    sms: false,
  },
  preferences: {
    language: 'en',
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
  },
  privacy: {
    profileVisibility: 'public',
    showEmail: false,
    showPhone: false,
  },
  accessibility: {
    fontSize: 'medium',
    highContrast: false,
    reduceMotion: false,
  },
};

/**
 * Settings slice
 */
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    // Notification settings
    setEmailNotifications: (state, action: PayloadAction<boolean>) => {
      state.notifications.email = action.payload;
    },
    setPushNotifications: (state, action: PayloadAction<boolean>) => {
      state.notifications.push = action.payload;
    },
    setSmsNotifications: (state, action: PayloadAction<boolean>) => {
      state.notifications.sms = action.payload;
    },
    updateNotifications: (state, action: PayloadAction<Partial<SettingsState['notifications']>>) => {
      state.notifications = { ...state.notifications, ...action.payload };
    },

    // Preference settings
    setLanguage: (state, action: PayloadAction<string>) => {
      state.preferences.language = action.payload;
    },
    setTimezone: (state, action: PayloadAction<string>) => {
      state.preferences.timezone = action.payload;
    },
    setDateFormat: (state, action: PayloadAction<string>) => {
      state.preferences.dateFormat = action.payload;
    },
    updatePreferences: (state, action: PayloadAction<Partial<SettingsState['preferences']>>) => {
      state.preferences = { ...state.preferences, ...action.payload };
    },

    // Privacy settings
    setProfileVisibility: (state, action: PayloadAction<SettingsState['privacy']['profileVisibility']>) => {
      state.privacy.profileVisibility = action.payload;
    },
    setShowEmail: (state, action: PayloadAction<boolean>) => {
      state.privacy.showEmail = action.payload;
    },
    setShowPhone: (state, action: PayloadAction<boolean>) => {
      state.privacy.showPhone = action.payload;
    },
    updatePrivacy: (state, action: PayloadAction<Partial<SettingsState['privacy']>>) => {
      state.privacy = { ...state.privacy, ...action.payload };
    },

    // Accessibility settings
    setFontSize: (state, action: PayloadAction<SettingsState['accessibility']['fontSize']>) => {
      state.accessibility.fontSize = action.payload;
    },
    setHighContrast: (state, action: PayloadAction<boolean>) => {
      state.accessibility.highContrast = action.payload;
    },
    setReduceMotion: (state, action: PayloadAction<boolean>) => {
      state.accessibility.reduceMotion = action.payload;
    },
    updateAccessibility: (state, action: PayloadAction<Partial<SettingsState['accessibility']>>) => {
      state.accessibility = { ...state.accessibility, ...action.payload };
    },

    // Bulk update
    updateAllSettings: (state, action: PayloadAction<Partial<SettingsState>>) => {
      return { ...state, ...action.payload };
    },

    // Reset to defaults
    resetSettings: () => initialState,
  },
});

// Export actions
export const {
  setEmailNotifications,
  setPushNotifications,
  setSmsNotifications,
  updateNotifications,
  setLanguage,
  setTimezone,
  setDateFormat,
  updatePreferences,
  setProfileVisibility,
  setShowEmail,
  setShowPhone,
  updatePrivacy,
  setFontSize,
  setHighContrast,
  setReduceMotion,
  updateAccessibility,
  updateAllSettings,
  resetSettings,
} = settingsSlice.actions;

// Selectors
export const selectSettings = (state: RootState) => state.settings;
export const selectNotifications = (state: RootState) => state.settings.notifications;
export const selectPreferences = (state: RootState) => state.settings.preferences;
export const selectPrivacy = (state: RootState) => state.settings.privacy;
export const selectAccessibility = (state: RootState) => state.settings.accessibility;

export default settingsSlice.reducer;
